
import React, { useState, useEffect, useRef } from 'react';
import { 
  Hammer, 
  X, 
  Clock, 
  Users, 
  TrendingUp, 
  Sparkles, 
  ChevronUp, 
  Loader2, 
  Volume2, 
  VolumeX, 
  MessageSquare,
  Award,
  Zap
} from 'lucide-react';
import { AuctionEvent, Product, User } from '../types';
import { auctionService } from '../services/auctionService';
import { useCurrency } from '../contexts/CurrencyContext';

interface Props {
  auction: AuctionEvent;
  product: Product;
  user: User | null;
  onClose: () => void;
  onWinner: (amount: number) => void;
}

const LiveAuctionModal: React.FC<Props> = ({ auction, product, user, onClose, onWinner }) => {
  const [currentAuction, setCurrentAuction] = useState(auction);
  const [isMuted, setIsMuted] = useState(true);
  const [timeLeft, setTimeLeft] = useState('');
  const [isBidding, setIsBidding] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [chatMessages, setChatMessages] = useState<{name: string, text: string}[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { format } = useCurrency();

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const end = new Date(currentAuction.endTime).getTime();
      const diff = end - now;

      if (diff <= 0) {
        clearInterval(timer);
        handleAuctionEnd();
        return;
      }

      const m = Math.floor((diff / 1000 / 60) % 60);
      const s = Math.floor((diff / 1000) % 60);
      setTimeLeft(`${m}:${s.toString().padStart(2, '0')}`);
      
      // Simulate random chat
      if (Math.random() > 0.95) {
         const names = ['Rahul', 'Amit', 'Sneha', 'Vikram', 'Priya'];
         const texts = ['Bhai ye mera hai!', 'Amazing price', 'Going once...', 'Last bid incoming!'];
         setChatMessages(prev => [...prev.slice(-4), { 
           name: names[Math.floor(Math.random() * names.length)], 
           text: texts[Math.floor(Math.random() * texts.length)] 
         }]);
      }
    }, 1000);

    const handleUpdate = () => {
      const updated = auctionService.getActiveAuction();
      if (updated && updated.id === currentAuction.id) setCurrentAuction(updated);
    };

    window.addEventListener('auction-update', handleUpdate);
    return () => {
      clearInterval(timer);
      window.removeEventListener('auction-update', handleUpdate);
    };
  }, [currentAuction.id, currentAuction.endTime]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [chatMessages]);

  const handleAuctionEnd = () => {
    const lastBid = currentAuction.bids[currentAuction.bids.length - 1];
    if (lastBid && lastBid.userId === user?.id) {
       setShowCelebration(true);
       setTimeout(() => onWinner(lastBid.amount), 3000);
    } else {
       alert("Auction Ended! Better luck next time bhai.");
       onClose();
    }
  };

  const handlePlaceBid = async () => {
    if (!user) return alert("Pehle login karo bhai!");
    setIsBidding(true);
    const nextAmount = currentAuction.currentPrice + currentAuction.bidIncrement;
    auctionService.placeBid(currentAuction.id, user.id, user.email.split('@')[0], nextAmount);
    setIsBidding(false);
  };

  return (
    <div className="fixed inset-0 z-[250] bg-black flex flex-col overflow-hidden animate-in fade-in duration-500">
       {/* Background Video (Simulated Live) */}
       <div className="absolute inset-0">
          <video 
            src={currentAuction.videoUrl || 'https://assets.mixkit.co/videos/preview/mixkit-circuit-board-details-4431-large.mp4'} 
            autoPlay loop muted={isMuted} playsInline className="w-full h-full object-cover opacity-60" 
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/60"></div>
       </div>

       {/* Top Bar HUD */}
       <div className="relative p-6 flex justify-between items-start z-10">
          <div className="flex flex-col gap-3">
             <div className="flex items-center gap-3">
                <div className="bg-red-600 text-white px-4 py-1.5 rounded-xl text-xs font-black tracking-widest flex items-center gap-2 animate-pulse shadow-lg shadow-red-600/50">
                   <div className="w-2 h-2 bg-white rounded-full"></div> LIVE AUCTION
                </div>
                <div className="bg-black/40 backdrop-blur-md border border-white/10 text-white px-4 py-1.5 rounded-xl text-xs font-black flex items-center gap-2">
                   <Users className="h-4 w-4" /> {Math.floor(Math.random() * 200) + 42}
                </div>
             </div>
             <div className="bg-white/10 backdrop-blur-xl border border-white/10 p-4 rounded-[2rem] flex items-center gap-4">
                <div className="w-14 h-14 bg-white rounded-2xl overflow-hidden shrink-0 shadow-2xl">
                   <img src={product.images[0]} className="w-full h-full object-cover" />
                </div>
                <div>
                   <p className="text-white font-black text-sm truncate w-40">{product.name}</p>
                   <p className="text-blue-400 font-bold text-[10px] uppercase tracking-widest">Base: {format(currentAuction.startingPrice)}</p>
                </div>
             </div>
          </div>
          <div className="flex gap-3">
             <button onClick={() => setIsMuted(!isMuted)} className="p-4 bg-white/10 backdrop-blur-xl rounded-full text-white hover:bg-white/20 transition-all border border-white/10">
                {isMuted ? <VolumeX className="h-6 w-6" /> : <Volume2 className="h-6 w-6" />}
             </button>
             <button onClick={onClose} className="p-4 bg-white/10 backdrop-blur-xl rounded-full text-white hover:bg-white/20 transition-all border border-white/10">
                <X className="h-6 w-6" />
             </button>
          </div>
       </div>

       {/* Center Overlay for Celebration */}
       {showCelebration && (
         <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-blue-600/40 backdrop-blur-xl animate-in zoom-in duration-500">
            <div className="text-center space-y-6">
               <div className="w-32 h-32 bg-white rounded-[3rem] flex items-center justify-center mx-auto shadow-2xl animate-bounce">
                  <Award className="h-16 w-16 text-blue-600" />
               </div>
               <h2 className="text-6xl font-black text-white tracking-tighter">YOU WON!</h2>
               <p className="text-2xl text-blue-100 font-bold">Deploying your checkout link...</p>
            </div>
         </div>
       )}

       {/* Bottom Control Section */}
       <div className="mt-auto relative z-10 p-6 space-y-6">
          {/* Bid History (Floating Chat Style) */}
          <div ref={scrollRef} className="max-w-xs space-y-3 h-48 overflow-y-auto scrollbar-hide">
             {chatMessages.map((msg, i) => (
               <div key={i} className="flex items-center gap-2 animate-in slide-in-from-left duration-300">
                  <div className="w-6 h-6 bg-blue-600 rounded-lg flex items-center justify-center text-[10px] font-black text-white shrink-0">{msg.name[0]}</div>
                  <div className="bg-black/40 backdrop-blur-md px-3 py-1.5 rounded-2xl border border-white/5">
                     <p className="text-xs text-white"><span className="font-black text-blue-400">{msg.name}:</span> {msg.text}</p>
                  </div>
               </div>
             ))}
             {currentAuction.bids.slice(-3).map((bid, i) => (
               <div key={bid.id} className="flex items-center gap-2 animate-in slide-in-from-left duration-300">
                  <div className="w-6 h-6 bg-emerald-500 rounded-lg flex items-center justify-center text-[10px] font-black text-white shrink-0"><Hammer className="h-3 w-3" /></div>
                  <div className="bg-emerald-500/20 backdrop-blur-md px-3 py-1.5 rounded-2xl border border-emerald-500/20 ring-1 ring-emerald-500/40">
                     <p className="text-xs text-white"><span className="font-black text-emerald-400">{bid.userName} bid:</span> {format(bid.amount)}</p>
                  </div>
               </div>
             ))}
          </div>

          {/* Pricing HUD */}
          <div className="bg-white p-8 rounded-[3rem] shadow-2xl border border-gray-100 flex flex-col md:flex-row items-center justify-between gap-8">
             <div className="flex items-center gap-8 w-full md:w-auto">
                <div className="space-y-1">
                   <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">Highest Bid</p>
                   <div className="flex items-baseline gap-2">
                      <h3 className="text-5xl font-black text-gray-900 tracking-tighter">{format(currentAuction.currentPrice)}</h3>
                      <TrendingUp className="h-6 w-6 text-emerald-500 animate-bounce" />
                   </div>
                </div>
                <div className="h-12 w-px bg-gray-100 hidden md:block"></div>
                <div className="space-y-1">
                   <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">Ending In</p>
                   <div className="flex items-center gap-2 text-3xl font-black text-red-600 tabular-nums">
                      <Clock className="h-6 w-6" /> {timeLeft || '0:00'}
                   </div>
                </div>
             </div>

             <div className="flex gap-4 w-full md:w-auto">
                <button 
                  onClick={handlePlaceBid}
                  disabled={isBidding}
                  className="flex-grow md:flex-initial bg-blue-600 text-white px-12 py-5 rounded-[2rem] font-black text-lg shadow-2xl shadow-blue-200 hover:bg-blue-700 active:scale-95 transition-all flex items-center justify-center gap-3"
                >
                   {isBidding ? <Loader2 className="h-6 w-6 animate-spin" /> : <><Hammer className="h-6 w-6" /> Bid {format(currentAuction.currentPrice + currentAuction.bidIncrement)}</>}
                </button>
             </div>
          </div>
       </div>

       {/* Visual Indicators Overlay */}
       <div className="absolute inset-0 pointer-events-none border-[20px] border-white/5 rounded-[4rem]">
          <div className="w-full h-full relative">
             <div className="absolute top-10 right-10 flex flex-col items-end gap-2">
                <div className="bg-white/10 backdrop-blur-xl p-3 rounded-2xl border border-white/20">
                   <Zap className="h-6 w-6 text-yellow-400 animate-pulse" />
                </div>
                <p className="text-[8px] font-black text-white uppercase tracking-widest bg-black/40 px-2 py-1 rounded">High Interest</p>
             </div>
          </div>
       </div>
    </div>
  );
};

export default LiveAuctionModal;
